import type {
  AvailabilitySlot,
  CourtResponse,
} from "@bw/shared/types/generated";

/**
 * Pure, React-free grid builder for the court sheet.
 *
 * Binding decisions (plan.availability_contract + sheetModel.test):
 *   - SLOT GRANULARITY IS 1 HOUR. The backend steps availability in 60-min
 *     windows; 09:00–22:00 → 13 hourly rows (last row 21:00–22:00).
 *   - Courts are the COLUMNS in backend order; only ENABLED courts appear.
 *   - `is_available === false` marks EXACTLY that (court, slot) cell
 *     unavailable. A row with NO matching slot is ALSO not-open — we never
 *     invent availability the backend did not return.
 *   - No server-returned slot for the selected date → `rows: []`,
 *     `isClosed: true`. Facility owns the effective policy; the client never
 *     recreates weekly, seasonal, or exception rules merely to draw the axis.
 *
 * Times are read in VENUE-LOCAL time. The availability slot `start_time` is an
 * ISO datetime; we resolve its display label in the facility timezone so the grid axis
 * matches the venue clock regardless of the viewer's browser tz.
 */

export interface SheetColumn {
  courtId: string;
  name: string;
  tag: string | null;
}

export interface SheetCell {
  courtId: string;
  /** The matching availability slot, if the backend returned one for this row. */
  slot: AvailabilitySlot | null;
  /**
   * Open iff the backend returned a slot for this (court, row), it is_available,
   * AND its start time has not already elapsed. A past slot is never open.
   */
  isAvailable: boolean;
  /**
   * True when this slot's start time is at or before "now" — i.e.
   * it has already begun and can no longer be booked. Comparison is on absolute
   * instants, so a slot on a future calendar day is never past even if its
   * wall-clock label is earlier than the current one. Rendered as a disabled,
   * visually-marked "Past" cell; the page also refuses to add it to the cart.
   */
  isPast: boolean;
}

export interface SheetRow {
  /** Exact UTC slot start emitted by Facility availability. */
  startTime: string;
  /** Human venue-local label, including minutes for a non-whole-hour slot. */
  label: string;
  cells: SheetCell[];
}

export interface SheetModel {
  date: string;
  timezone: string;
  columns: SheetColumn[];
  rows: SheetRow[];
  /** True when Facility returned no operating slots for the selected date. */
  isClosed: boolean;
}

export interface BuildSheetModelArgs {
  date: string;
  timezone: string;
  courts: CourtResponse[];
  slots: AvailabilitySlot[];
  /**
   * The reference "now" used to gate already-elapsed slots. Injectable so the
   * grid is deterministically testable with a fixed clock; defaults to the real
   * wall clock. A slot is "past" when its start instant is <= now.
   */
  now?: Date;
}

function venueLocalLabel(iso: string, timezone: string): string | null {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  try {
    return new Intl.DateTimeFormat("en-US", {
      timeZone: timezone,
      hour: "numeric",
      minute: "2-digit",
    }).format(d);
  } catch {
    return null;
  }
}

export function buildSheetModel(args: BuildSheetModelArgs): SheetModel {
  const {
    date,
    timezone,
    courts,
    slots,
    now = new Date(),
  } = args;
  const nowMs = now.getTime();

  // Columns: enabled courts only, preserving the backend's canonical order.
  // Ordering is derived server-side and may reflect natural names or future
  // staff configuration; the presentation layer must not reinterpret it.
  const columns: SheetColumn[] = courts
    .filter((c) => c.status === "enabled")
    .map((c) => ({
      courtId: c.court_id,
      name: c.name,
      tag: c.tags && c.tags.length > 0 ? c.tags[0] : null,
    }));

  const startsByMillis = new Map<number, string>();
  for (const slot of slots) {
    const millis = new Date(slot.start_time).getTime();
    if (!Number.isNaN(millis)) {
      startsByMillis.set(millis, slot.start_time);
    }
  }
  const orderedStarts = [...startsByMillis.entries()].sort(
    ([left], [right]) => left - right,
  );
  if (orderedStarts.length === 0) {
    return { date, timezone, columns, rows: [], isClosed: true };
  }

  // Index slots by the exact server-returned instant. A seasonal opening at
  // 08:30 is a distinct row from 08:00; collapsing either to an hour would
  // mislabel or overwrite a bookable slot.
  const slotIndex = new Map<string, AvailabilitySlot>();
  for (const s of slots) {
    const millis = new Date(s.start_time).getTime();
    if (!Number.isNaN(millis)) {
      slotIndex.set(`${s.court_id}@${millis}`, s);
    }
  }

  const rows: SheetRow[] = [];
  for (const [startMillis, startTime] of orderedStarts) {
    const label = venueLocalLabel(startTime, timezone);
    if (label === null) continue;
    const cells: SheetCell[] = columns.map((col) => {
      const slot = slotIndex.get(`${col.courtId}@${startMillis}`) ?? null;
      const isPast = startMillis <= nowMs;
      return {
        courtId: col.courtId,
        slot,
        // Open iff the backend gave us a slot, it is_available, AND it has not
        // already started. Never invent; never offer an elapsed slot.
        isAvailable: slot !== null && slot.is_available === true && !isPast,
        isPast,
      };
    });
    rows.push({ startTime, label, cells });
  }

  return { date, timezone, columns, rows, isClosed: false };
}
